import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getLicense, updateLicense } from "../../api/Licenses";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import EditForm from "../../components/EditForm";
import { getBillingGroupsByLicense } from "../../api/BillingGroups"; // Import the API function
import Breadcrumbs from "../../components/Breadcrumbs";

const EditLicensesPage = () => {
  const { licensePublicId } = useParams();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loadingLicense, setLoadingLicense] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);

  useEffect(() => {
    handleGetLicense();
    handleGetBillingGroups();
  }, [licensePublicId]);

  const handleGetLicense = async () => {
    try {
      const accessToken = await getAccessToken();

      const newLicense = await getLicense(licensePublicId, accessToken);
      setLicense(newLicense);
      setLoadingLicense(false);
    } catch (error) {
      console.error("Failed to fetch license:", error);
      setLoadingLicense(false);
    }
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByLicense(
        licensePublicId,
        accessToken
      );
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

/*   const handleBillingGroupChange = (option) => {
    const selectedGroup = billingGroups.find((group) => group.name === option);
    if (!selectedGroup) return;
    setLicense((prevLicense) => ({
      ...prevLicense,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    setHasChanges(true);
  }; */

  const handleUpdateLicense = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!license.billingGroupPublicId) {
        console.error("❌ Missing billingGroupPublicId! API call might fail.");
        return;
      }

      // Update the billing group first (if it has changed)
      if (license.billingGroupPublicId) {
        const updatedLicense = await updateLicense(
          license.billingGroupPublicId,
          license.licensePublicId,
          accessToken
        );

        setLicense(updatedLicense); // Update the user state with the new data
      }

      setHasChanges(false); // Reset change state

      handleGetLicense();

      toast.success("License updated successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      console.error("Failed to update license:", error);
      toast.error("Failed to update license");
    }
  };

  const saveAndExit = () => {
    handleUpdateLicense();
  };

  return (
    <>
      <HeaderBar />

      <div className="flex flex-row w-full">
        <Sidebar />

        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Licenses"} loading={loadingLicense} />
          </div>

          <div className={`${loadingLicense ? "loading" : ""} page-content`}>
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit license: {license?.name}
              </h2>

              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={saveAndExit}
                >
                  Update
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={license || {}}
                setData={setLicense}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  {
                    key: "billingGroupName",
                    label: "Billing Group",
                    type: "dropdown",
                    disabled: false,
                  },
                  {
                    key: "licensePublicId",
                    label: "License ID",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdBy",
                    label: "Created by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdDateTimeUTC",
                    label: "Created date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "expirationDate",
                    label: "Expiration date",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "price",
                    label: "Price",
                    type: "number",
                    disabled: true,
                  },
                  {
                    key: "quantity",
                    label: "Quantity",
                    type: "number",
                    disabled: true,
                  },
                  {
                    key: "updatedBy",
                    label: "Updated by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDateTimeUTC",
                    label: "Updated date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "validationPeriod",
                    label: "Validation period",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "accountEnabled",
                    label: "On-premises sync",
                    type: "switch",
                    disabled: true,
                  },
                  {
                    key: "name",
                    label: "License name",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDate",
                    label: "Last updated",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "accountTypeName",
                    label: "Account type",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "assignedToUser",
                    label: "Assigned to user",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "subscriptionRenewalDate",
                    label: "Date for subscription renewal",
                    type: "date",
                    disabled: true,
                  },
                ]}
                dropdownOptions={{
                  billingGroupName: billingGroups.map((g) => ({
                    name: g.name, // Ensure "name" field exists
                    billingGroupPublicId: g.billingGroupPublicId, // Ensure ID field exists
                  })),
                }}
              />

              {/*               <LicenseForm
                license={license || {}}
                setLicense={setLicense}
                setHasChanges={setHasChanges}
                disabled={true}
                billingGroups={billingGroups}
                onBillingGroupChange={handleBillingGroupChange}
              /> */}
            </div>
          </div>
        </main>

        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditLicensesPage;
